import { createContext, useState, useEffect, useCallback } from 'react';
import {
    getToken,
    getCurrentUser,
    storeAuthData,
    clearAuthData,
    login as authLogin,
    register as authRegister
} from '../services/authService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [logoutTimer, setLogoutTimer] = useState(null);

    const AUTO_LOGOUT_TIME = 15 * 60 * 1000;

    const resetLogoutTimer = useCallback(() => {
        if (logoutTimer) {
            clearTimeout(logoutTimer);
        }

        if (isAuthenticated) {
            const timer = setTimeout(() => {
                console.log('Auto-logout due to inactivity');
                logout();
            }, AUTO_LOGOUT_TIME);

            setLogoutTimer(timer);
        }
    }, [isAuthenticated, logoutTimer]);

    const handleUserActivity = useCallback(() => {
        if (isAuthenticated) {
            resetLogoutTimer();
        }
    }, [isAuthenticated, resetLogoutTimer]);

    const checkAuthStatus = () => {
        setIsLoading(true);
        try {
            const token = getToken();
            const currentUser = getCurrentUser();

            if (token && currentUser) {
                setUser(currentUser);
                setIsAuthenticated(true);
                resetLogoutTimer();
            } else {
                setUser(null);
                setIsAuthenticated(false);
                clearAuthData();
            }
        } catch (error) {
            console.error('Error checking auth status:', error);
            setUser(null);
            setIsAuthenticated(false);
            clearAuthData();
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (userData) => {
        try {
            const response = await authRegister(userData);
            if (response.success) {
                const { token, user } = response.data;
                storeAuthData(token, user);
                setUser(user);
                setIsAuthenticated(true);
                resetLogoutTimer();
                return { success: true, user };
            }
            throw new Error(response.message || 'Registration failed');
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const login = async (credentials, expectedUserType) => {
        try {
            const response = await authLogin(credentials);

            if (response.success) {
                const userData = response.data.user;
                const token = response.data.token;
                const userType = userData.userType;

                if (expectedUserType && userType !== expectedUserType) {
                    clearAuthData();
                    throw new Error(
                        userType === 'celeb'
                            ? 'Please use the celebrity login page'
                            : 'Please use the fan login page'
                    );
                }

                if (token && userData) {
                    storeAuthData(token, userData);
                    setUser(userData);
                    setIsAuthenticated(true);
                    resetLogoutTimer();
                    return { success: true, user: userData, userType };
                }
                throw new Error('Authentication data missing');
            }
            throw new Error(response.message || 'Login failed');
        } catch (error) {
            console.error('Login error in context:', error);
            clearAuthData();
            setUser(null);
            setIsAuthenticated(false);
            throw error;
        }
    };

    const logout = () => {
        if (logoutTimer) {
            clearTimeout(logoutTimer);
            setLogoutTimer(null);
        }

        clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
    };

    useEffect(() => {
        if (isAuthenticated) {
            const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

            events.forEach(event => {
                document.addEventListener(event, handleUserActivity, { passive: true });
            });

            return () => {
                events.forEach(event => {
                    document.removeEventListener(event, handleUserActivity);
                });
            };
        }
    }, [isAuthenticated, handleUserActivity]);

    useEffect(() => {
        return () => {
            if (logoutTimer) {
                clearTimeout(logoutTimer);
            }
        };
    }, [logoutTimer]);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                isLoading,
                login,
                logout,
                register,
                checkAuthStatus
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};


// import { createContext, useState, useEffect } from 'react';
// import {
//     getToken,
//     getCurrentUser,
//     storeAuthData,
//     clearAuthData,
//     login as authLogin,
//     register as authRegister
// } from '../services/authService';

// export const AuthContext = createContext();

// export const AuthProvider = ({ children }) => {
//     const [user, setUser] = useState(null);
//     const [isAuthenticated, setIsAuthenticated] = useState(false);
//     const [isLoading, setIsLoading] = useState(true);

//     // Function to check authentication status
//     const checkAuthStatus = () => {
//         setIsLoading(true);
//         try {
//             const token = getToken();
//             const currentUser = getCurrentUser();

//             console.log('Auth check - Token exists:', !!token);
//             console.log('Auth check - User exists:', !!currentUser);

//             if (token && currentUser) {
//                 setUser(currentUser);
//                 setIsAuthenticated(true);
//                 console.log('User is authenticated');
//             } else {
//                 setUser(null);
//                 setIsAuthenticated(false);
//                 console.log('User is not authenticated');
//                 // Clear any invalid auth data
//                 clearAuthData();
//             }
//         } catch (error) {
//             console.error('Error checking auth status:', error);
//             setUser(null);
//             setIsAuthenticated(false);
//             clearAuthData();
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     useEffect(() => {
//         checkAuthStatus();
//         return () => { };
//     }, []);

//     const register = async (userData) => {
//         try {
//             const response = await authRegister(userData);
//             if (response.success) {
//                 const { token, user } = response.data;
//                 storeAuthData(token, user);
//                 setUser(user);
//                 setIsAuthenticated(true);
//                 return { success: true, user };
//             }
//             throw new Error(response.message || 'Registration failed');
//         } catch (error) {
//             console.error('Registration error:', error);
//             throw error;
//         }
//     };

//     const login = async (credentials, expectedUserType) => {
//         try {
//             const response = await authLogin(credentials);
//             console.log('Login response in context:', response);

//             if (response.success) {
//                 const userData = response.data.user;
//                 const token = response.data.token;
//                 const userType = userData.userType;

//                 console.log('Login successful - UserType:', userType, 'Expected:', expectedUserType);

//                 // Check user type mismatch
//                 if (expectedUserType && userType !== expectedUserType) {
//                     clearAuthData();
//                     throw new Error(
//                         userType === 'celeb'
//                             ? 'Please use the celebrity login page'
//                             : 'Please use the fan login page'
//                     );
//                 }

//                 if (token && userData) {
//                     storeAuthData(token, userData);
//                     setUser(userData);
//                     setIsAuthenticated(true);
//                     return { success: true, user: userData, userType };
//                 }
//                 throw new Error('Authentication data missing');
//             }
//             throw new Error(response.message || 'Login failed');
//         } catch (error) {
//             console.error('Login error in context:', error);
//             clearAuthData();
//             setUser(null);
//             setIsAuthenticated(false);
//             throw error;
//         }
//     };

//     const logout = () => {
//         clearAuthData();
//         setUser(null);
//         setIsAuthenticated(false);
//     };

//     return (
//         <AuthContext.Provider
//             value={{
//                 user,
//                 isAuthenticated,
//                 isLoading,
//                 login,
//                 logout,
//                 register,
//                 checkAuthStatus
//             }}
//         >
//             {children}
//         </AuthContext.Provider>
//     );
// };